import { useState, useEffect } from 'react';

interface VeiculoInfo {
  ticket: string;
  placa: string;
  entrada: string;
  tipo: string;
  validado: string;
  raia: string;
}

export default function EstacionamentoPublicoPage() {
  // Redirecionamento automático para o site externo
  useEffect(() => {
    window.location.href = 'https://estacionamento.centrodacoroa.com.br/consultar';
  }, []);

  const [placa, setPlaca] = useState('');
  const [loading, setLoading] = useState(false);
  const [veiculoInfo, setVeiculoInfo] = useState<VeiculoInfo | null>(null);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [horarioSaida, setHorarioSaida] = useState('');
  const [processandoPagamento, setProcessandoPagamento] = useState(false);
  const [ticketData, setTicketData] = useState<VeiculoInfo | null>(null);
  const [valorAPagar, setValorAPagar] = useState<number>(15);
  const [mensagemPagamento, setMensagemPagamento] = useState<string>('');
  const [pagamentoAprovado, setPagamentoAprovado] = useState(false);
  const [validandoTicket, setValidandoTicket] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);

  // Verificar retorno do pagamento na URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const ticketParam = params.get('ticket');
    const placaParam = params.get('placa');

    console.log('🔍 Verificando URL:', {
      paymentStatus,
      ticketParam,
      placaParam,
      fullURL: window.location.href
    });

    if (paymentStatus === 'success' && ticketParam && placaParam) {
      console.log('✅ Pagamento aprovado detectado!');
      setPagamentoAprovado(true);
      setMostrarModal(true);
      setTicketData({
        ticket: ticketParam,
        placa: placaParam,
        entrada: '',
        tipo: '',
        validado: 'N',
        raia: ''
      });
      setSucesso(`Pagamento confirmado! Ticket ${ticketParam} - Placa ${placaParam}`);
      
      // Limpa os parâmetros da URL após 2 segundos
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 2000);
    } else if (paymentStatus === 'cancelled') {
      console.log('❌ Pagamento cancelado');
      setErro('O pagamento foi cancelado. Tente novamente.');
      
      // Limpa os parâmetros da URL
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 3000);
    }
  }, []);

  // Log do estado para debug
  useEffect(() => {
    console.log('📊 Estado atual:', {
      pagamentoAprovado,
      ticketData,
      sucesso,
      mostrarModal
    });
  }, [pagamentoAprovado, ticketData, sucesso, mostrarModal]);

  // Funções auxiliares e de manipulação

  const formatarPlaca = (valor: string) => {
    const limpo = valor.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (limpo.length <= 3) return limpo;
    return `${limpo.slice(0, 3)}-${limpo.slice(3, 7)}`;
  };

  const formatarDataHora = (dataStr: string) => {
    try {
      if (dataStr.includes('/')) {
        return dataStr;
      }
      
      const [data, hora] = dataStr.split(' ');
      
      if (data.includes('-')) {
        const partes = data.split('-');
        if (partes[0].length === 4) {
          const [ano, mes, dia] = partes;
          return `${dia}/${mes}/${ano} ${hora}`;
        } else {
          const [dia, mes, ano] = partes;
          return `${dia}/${mes}/${ano} ${hora}`;
        }
      }
      
      return dataStr;
    } catch {
      return dataStr;
    }
  };

  const calcularHorarioSaida = () => {
    const agora = new Date();
    const saida = new Date(agora.getTime() + 15 * 60 * 1000);
    
    const dia = String(saida.getDate()).padStart(2, '0');
    const mes = String(saida.getMonth() + 1).padStart(2, '0');
    const ano = saida.getFullYear();
    const hora = String(saida.getHours()).padStart(2, '0');
    const min = String(saida.getMinutes()).padStart(2, '0');
    const seg = String(saida.getSeconds()).padStart(2, '0');
    
    return `${dia}/${mes}/${ano} ${hora}:${min}:${seg}`;
  };

  const handlePlacaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = formatarPlaca(e.target.value);
    setPlaca(valorFormatado);
    setErro('');
    setSucesso('');
  };

  const handleConsultar = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (placa.length < 7) {
      setErro('Digite uma placa válida');
      return;
    }

    setLoading(true);
    setErro('');
    setSucesso('');
    setVeiculoInfo(null);
    setHorarioSaida('');

    try {
      const placaLimpa = placa.replace('-', '');
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/consulta-estacionamento?placa=${placaLimpa}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.error) {
        setErro(data.error);
        return;
      }
      
      if (data && data.placa) {
        setVeiculoInfo(data);
        setTicketData(data);
      } else {
        setErro('Veículo não encontrado no estacionamento');
      }
    } catch (error) {
      console.error('Erro completo:', error);
      
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        setErro('Erro de conexão com o servidor. Verifique sua conexão.');
      } else if (error instanceof Error) {
        setErro(`Erro: ${error.message}`);
      } else {
        setErro('Erro ao consultar. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePagamentoStripe = async () => {
    if (!veiculoInfo) return;

    setProcessandoPagamento(true);
    setErro('');
    setMensagemPagamento('Preparando pagamento seguro...');

    try {
      console.log('🚀 Iniciando pagamento Stripe...');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/stripe-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            tipo: 'estacionamento',
            ticket: veiculoInfo.ticket,
            placa: veiculoInfo.placa,
            valor: 15.0,
            descricao: `Estacionamento - Placa ${veiculoInfo.placa}`,
          }),
        }
      );

      const data = await response.json();
      console.log('📦 Resposta recebida:', data);

      if (!response.ok) {
        const errorMessage = data.error || `Erro HTTP ${response.status}`;
        console.error('❌ Erro na resposta:', errorMessage);
        throw new Error(errorMessage);
      }

      if (!data.success) {
        const errorMessage = data.error || 'Erro desconhecido no pagamento';
        console.error('❌ Erro no pagamento:', errorMessage);
        throw new Error(errorMessage);
      }

      if (data.checkout_url) {
        console.log('✅ Redirecionando para:', data.checkout_url);
        setMensagemPagamento('Redirecionando para pagamento...');
        
        setTimeout(() => {
          if (window.top && window.top !== window) {
            window.top.location.href = data.checkout_url;
          } else {
            window.location.href = data.checkout_url;
          }
        }, 500);
      } else {
        throw new Error('URL de checkout não recebida do servidor');
      }

    } catch (error) {
      console.error('❌ Erro no pagamento:', error);
      
      let errorMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      setErro(errorMessage);
      setMensagemPagamento('');
      setProcessandoPagamento(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && placa.length >= 7 && !loading) {
      handleConsultar(e as any);
    }
  };

  const handleNovaConsulta = () => {
    setVeiculoInfo(null);
    setPlaca('');
    setSucesso('');
    setHorarioSaida('');
    setErro('');
    setProcessandoPagamento(false);
    setPagamentoAprovado(false);
    setTicketData(null);
    setMostrarModal(false);
  };

  const handleValidarTicket = async () => {
    if (!ticketData) return;

    setValidandoTicket(true);
    setErro('');
    setSucesso('');

    try {
      console.log('🎫 Validando ticket:', ticketData.ticket, 'Placa:', ticketData.placa);

      const placaLimpa = ticketData.placa.replace(/[^a-zA-Z0-9]/g, '');
      
      // Chama a Edge Function de validação (HTTPS)
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validar-ticket?placa=${placaLimpa}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      console.log('📥 Resposta da validação:', data);

      if (response.ok && data.success) {
        setSucesso('✅ Ticket validado com sucesso! Você pode retirar seu veículo agora.');
        setHorarioSaida(calcularHorarioSaida());
        setPagamentoAprovado(false);
        setMostrarModal(false);
      } else {
        setErro(data.error || 'Erro ao validar ticket. Tente novamente.');
      }

    } catch (error) {
      console.error('❌ Erro ao validar ticket:', error);
      setErro('Erro ao validar ticket. Tente novamente.');
    } finally {
      setValidandoTicket(false);
    }
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setPagamentoAprovado(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-green-100 to-green-200 flex items-center justify-center p-3 sm:p-4">
      {/* Loading durante o redirecionamento */}
      <div className="text-center">
        <div className="w-16 h-16 bg-green-600 rounded-full mx-auto mb-4 flex items-center justify-center">
          <i className="ri-loader-4-line text-white text-2xl animate-spin"></i>
        </div>
        <h2 className="text-2xl font-bold text-green-800 mb-2">
          Redirecionando...
        </h2>
        <p className="text-green-600">
          Você será redirecionado para o sistema de estacionamento
        </p>
      </div>
    </div>
  );
}
